import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { platformLimits } from '../data/platforms';
import { chips, computeProviders, modelPlatforms, OPENROUTER_FALLBACK_COUNT } from '../data/stack';
import { COMPILED_LABEL } from '../data/sources';
import { CountResult, getOpenRouterCount } from '../utils/api';
import { PageHeader } from '../components/PageHeader';
import { DataBadge } from '../components/DataBadge';
import { AgentPlatformsTab } from '../components/platforms/AgentPlatformsTab';
import { ModelPlatformsTab } from '../components/platforms/ModelPlatformsTab';
import { ComputeTab } from '../components/platforms/ComputeTab';
import { SiliconTab } from '../components/platforms/SiliconTab';
import { CountryViewTab } from '../components/platforms/CountryViewTab';
import { ecosystemPivot } from '../utils/ecosystem';
type TabId = 'agents' | 'models' | 'compute' | 'silicon' | 'countries';
const TABS: Array<{
  id: TabId;
  label: string;
  noun: string;
  count: number;
}> = [{
  id: 'agents',
  label: 'Agent platforms',
  noun: 'agent platforms',
  count: platformLimits.length
}, {
  id: 'models',
  label: 'Model platforms',
  noun: 'model platforms',
  count: modelPlatforms.length
}, {
  id: 'compute',
  label: 'Compute',
  noun: 'compute providers',
  count: computeProviders.length
}, {
  id: 'silicon',
  label: 'Silicon',
  noun: 'chips tracked',
  count: chips.length
}, {
  id: 'countries',
  label: 'Country view',
  noun: 'countries',
  count: ecosystemPivot({}).length
}];
export function Platforms() {
  const [params, setParams] = useSearchParams();
  const param = params.get('tab');
  const active: TabId = TABS.some(t => t.id === param) ? param as TabId : 'agents';
  const [openrouter, setOpenrouter] = useState<CountResult | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  useEffect(() => {
    let alive = true;
    getOpenRouterCount(OPENROUTER_FALLBACK_COUNT).then(r => alive && setOpenrouter(r));
    return () => {
      alive = false;
    };
  }, []);
  const select = (id: TabId) => setParams(id === 'agents' ? {} : {
    tab: id
  }, {
    replace: true
  });
  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;else if (e.key === 'ArrowLeft') next = (i - 1 + TABS.length) % TABS.length;else if (e.key === 'Home') next = 0;else if (e.key === 'End') next = TABS.length - 1;else return;
    e.preventDefault();
    select(TABS[next].id);
    tabRefs.current[next]?.focus();
  };
  return <>
      <PageHeader title="Platforms" lede="The full stack beneath the machine population — from the platforms where agents are made, to where models are served, to the clouds and chips that run them." data-id="element-1444" data-mp="1pb3ynxxmp6jr">
        <DataBadge mode="snapshot" source="Curated from vendor docs, filings & announcements" asOf={COMPILED_LABEL} data-id="element-1445" data-mp="3r1fcj926qhcg" />
        {openrouter && <DataBadge mode={openrouter.mode} source="OpenRouter" asOf={openrouter.asOf} data-id="element-1446" data-mp="3m45pg029qhrs" />}
      </PageHeader>

      <div className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-1447" data-mp="21lbpiu2v09dn">
        <p className="pt-6 font-mono text-[12px] text-muted" data-id="element-1448" data-mp="26kejnjhawv6u">
          {TABS.map((t, i) => <span key={t.id} data-id="element-1449" data-mp="1rvihg31xece5" data-mpi={i}>
              <span className="text-ink" data-id="element-1450" data-mp="1ru7chzwquylg">{t.count}</span> {t.noun}
              {i < TABS.length - 1 && <span className="mx-2 text-faint" data-id="element-1451" data-mp="1wt1vp2ymip63">·</span>}
            </span>)}
        </p>

        <div role="tablist" aria-label="Stack layer" className="mt-4 grid grid-cols-2 border-b border-line sm:grid-cols-3 lg:grid-cols-5" data-id="element-1452" data-mp="1it98viq7wt3e">
          {TABS.map((t, i) => {
          const selected = t.id === active;
          return <button key={t.id} ref={el => {
            tabRefs.current[i] = el;
          }} id={`tab-${t.id}`} type="button" role="tab" aria-selected={selected} aria-controls={`panel-${t.id}`} tabIndex={selected ? 0 : -1} onClick={() => select(t.id)} onKeyDown={e => onKeyDown(e, i)} className={`relative flex items-baseline justify-between gap-3 px-1 py-4 text-left transition-colors duration-150 md:px-4 ${selected ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-1453" data-mp="18yh7s4xffo3v" data-mpi={i}>
                <span className="whitespace-nowrap text-[14px]" data-id="element-1454" data-mp="u6pzqkda8vgt">{t.label}</span>
                <span className={`font-mono text-2xl tabular ${selected ? 'text-machine' : ''}`} data-id="element-1455" data-mp="p8wrllsf5rdw">{t.count}</span>
                {selected && <span className="absolute inset-x-0 -bottom-px h-[2px] bg-machine" aria-hidden="true" data-id="element-1456" data-mp="kg3n8z0v4l2c" />}
              </button>;
        })}
        </div>

        <div id={`panel-${active}`} role="tabpanel" aria-labelledby={`tab-${active}`} tabIndex={0} className="pt-10 focus:outline-none" data-id="element-1457" data-mp="bhrlq1ygk2v4">
          {active === 'agents' && <AgentPlatformsTab data-id="element-1458" data-mp="1ik8rfvmya8j" />}
          {active === 'models' && <ModelPlatformsTab openrouter={openrouter} data-id="element-1459" data-mp="1j2ed346u0t4" />}
          {active === 'compute' && <ComputeTab data-id="element-1460" data-mp="2d4ul1rt1whiq" />}
          {active === 'silicon' && <SiliconTab data-id="element-1461" data-mp="2d89xemvcbcgg" />}
          {active === 'countries' && <CountryViewTab data-id="element-1462" data-mp="2i3pbnfx9fade" />}
        </div>
      </div>
    </>;
}